import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { mensagemDeErro } from '@/lib/erros';
import { ApiError, request } from '@/lib/http';
import {
  cartaoCnpjSchema,
  type Cnae,
  type InscricaoEstadual,
  enderecoDoCepSchema,
  type EmpresaDetalhe,
  empresaDetalheSchema,
  municipioSchema,
  type RegimeVigencia,
} from '@fiscoai/contracts';

/** Quantos municípios por consulta: o combo busca no servidor, nunca carrega a lista inteira. */
export const MUNICIPIOS_POR_BUSCA = 20;

/**
 * Busca remota da cidade (cascata com o estado): sempre `uf` + `q` + `limit`. Função de módulo —
 * identidade estável, como o `SelectPesquisavel` remoto exige.
 */
export function buscarMunicipios(uf: string, termo: string, signal: AbortSignal) {
  return request(`/localidades/ufs/${uf}/municipios`, {
    schema: z.array(municipioSchema),
    query: { q: termo, limit: MUNICIPIOS_POR_BUSCA },
    signal,
  });
}

export function useBuscarCep() {
  return useMutation({
    mutationFn: (cep: string) =>
      request(`/localidades/cep/${cep.replace(/\D/g, '')}`, { schema: enderecoDoCepSchema }),
  });
}

export function useCartaoCnpj() {
  return useMutation({
    mutationFn: (cnpj: string) =>
      request(`/cnpj/${encodeURIComponent(cnpj)}`, { schema: cartaoCnpjSchema }),
  });
}

/** Mensagem das consultas externas: 404 e 503 têm texto próprio; nunca bloqueiam o manual. */
export function mensagemDeConsulta(erro: unknown, oQue: 'CEP' | 'CNPJ'): string {
  if (erro instanceof ApiError && erro.status === 404) {
    return oQue === 'CEP' ? 'CEP não encontrado' : 'CNPJ não encontrado na Receita';
  }
  if (erro instanceof ApiError && erro.status === 503) {
    return 'Consulta indisponível — preencha manualmente';
  }
  return mensagemDeErro(erro);
}

/** Grava CNAEs e (opcional) regimes de uma empresa recém-criada a partir do cartão. */
export async function completarComCartao(
  empresaId: string,
  dados: {
    cnaes: readonly Cnae[];
    regimes: readonly RegimeVigencia[];
    inscricoes: readonly InscricaoEstadual[];
  },
): Promise<EmpresaDetalhe | null> {
  let detalhe: EmpresaDetalhe | null = null;
  if (dados.cnaes.length > 0) {
    detalhe = await request(`/empresas/${empresaId}/cnaes`, {
      method: 'PUT',
      body: { itens: dados.cnaes },
      schema: empresaDetalheSchema,
    });
  }
  if (dados.inscricoes.length > 0) {
    detalhe = await request(`/empresas/${empresaId}/inscricoes-estaduais`, {
      method: 'PUT',
      body: { itens: dados.inscricoes },
      schema: empresaDetalheSchema,
    });
  }
  if (dados.regimes.length > 0) {
    detalhe = await request(`/empresas/${empresaId}/regimes`, {
      method: 'PUT',
      body: { itens: dados.regimes },
      schema: empresaDetalheSchema,
    });
  }
  return detalhe;
}
